import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, EnvelopeIcon, PhoneIcon, MapPinIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { consumeRegistrationInvitation, getPublicEventSettings, validateRegistrationInvitation } from '../../services/eventApi';
import PhoneInput from '../Common/PhoneInput';
import { validateStoredPhoneValue } from '../../utils/phoneUtils';
import { getSlimLogoPath } from '../../utils/slimLogo';

const DEFAULT_TERMS_TEXT = `These general terms and conditions govern your use of this website and any services made available through it. By creating an account or accessing the site, you agree to comply with these terms.

Use of the website
You agree to use this website only for lawful purposes and in a way that does not infringe the rights of, restrict or inhibit anyone else's use and enjoyment of the website.

Account responsibility
You are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You must notify the site owner immediately of any unauthorized use of your account.

Content and availability
The website and its content are provided on an "as is" and "as available" basis. The site owner may change, suspend, or discontinue any part of the website at any time without notice.

Limitation of liability
To the maximum extent permitted by law, the site owner will not be liable for any indirect, incidental, or consequential damages arising out of or in connection with your use of the website.

Changes to these terms
The site owner may update these terms from time to time. Continued use of the website after changes are posted constitutes your acceptance of the revised terms.`;

const Register = () => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = String(searchParams.get('invite') || '').trim();
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken));
  const [inviteInfo, setInviteInfo] = useState(null);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      /** Public sign-up is external-company/agency only (user-type UI hidden). */
      userType: 'EXTERNAL',
      name: '',
      firstName: '',
      lastName: '',
      mobile: '',
      address: '',
      companyName: '',
      companyWebsite: '',
      companyType: 'COMPANY',
      companyOrigin: '',
      vatRegistrationNo: '',
      tin: '',
      telephone: '',
      ceoName: '',
      ceoEmail: '',
      ceoMobile: '',
      agencyNetwork: '',
      agencyHoldingCompany: '',
      brandsProducts: '',
      brandCategory: '',
      brandWebsite: '',
      email: '',
      password: '',
      confirmPassword: '',
      acceptTerms: false
    },
    shouldUnregister: true
  });

  const passwordValue = watch('password');
  const userType = watch('userType');
  const isExternalUser = userType === 'EXTERNAL';
  const companyType = watch('companyType');
  const companyOrigin = watch('companyOrigin');
  const entityLabel = companyType === 'AGENCY' ? 'Agency' : 'Company';

  // Contact details and T&C fetched from the Events settings (public endpoint)
  const [contactInfo, setContactInfo] = useState(null);        // event-specific contact
  const [generalContactInfo, setGeneralContactInfo] = useState(null); // general / system contact
  const [termsText, setTermsText] = useState(DEFAULT_TERMS_TEXT);

  useEffect(() => {
    let cancelled = false;
    getPublicEventSettings()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        // Event contact (amber banner, external users only)
        setContactInfo(data?.configured ? data : false);
        // General contact (indigo banner, all users)
        setGeneralContactInfo(
          data?.generalConfigured
            ? {
                contactPersonName: data.generalContactPersonName,
                contactEmail: data.generalContactEmail,
                contactPhones: data.generalContactPhones,
                address: data.generalContactAddress,
              }
            : false
        );
        if (data?.termsAndConditions) {
          setTermsText(data.termsAndConditions);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setContactInfo(false);
          setGeneralContactInfo(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!inviteToken) {
      setInviteLoading(false);
      setInviteInfo(null);
      return;
    }
    let cancelled = false;
    setInviteLoading(true);
    validateRegistrationInvitation(inviteToken)
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data || {};
        setInviteInfo(data);
        if (data.valid && data.recipientEmail) {
          setValue('email', String(data.recipientEmail).toLowerCase());
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInviteInfo({ valid: false, message: 'Could not validate this registration link.' });
        }
      })
      .finally(() => {
        if (!cancelled) setInviteLoading(false);
      });
    return () => { cancelled = true; };
  }, [inviteToken, setValue]);

  const inviteEmailLocked = Boolean(inviteToken && inviteInfo?.valid && inviteInfo?.recipientEmail);

  const onSubmit = async (data) => {
    const { userType, email, password, confirmPassword, acceptTerms } = data;

    const payload =
      userType === 'EXTERNAL'
        ? {
            userType: 'EXTERNAL',
            firstName: data.firstName,
            lastName: data.lastName,
            mobile: data.mobile,
            address: data.address,
            companyName: data.companyName,
            companyWebsite: data.companyWebsite,
            companyType: data.companyType,
            companyOrigin: data.companyOrigin,
            vatRegistrationNo: String(data.vatRegistrationNo || '').trim(),
            tin: String(data.tin || '').trim(),
            telephone: String(data.telephone || '').trim(),
            ceoName: String(data.ceoName || '').trim(),
            ceoEmail: String(data.ceoEmail || '').trim(),
            ceoMobile: String(data.ceoMobile || '').trim(),
            agencyNetwork: data.companyType === 'AGENCY' ? String(data.agencyNetwork || '').trim() : '',
            agencyHoldingCompany: data.companyType === 'AGENCY' ? String(data.agencyHoldingCompany || '').trim() : '',
            brandsProducts: String(data.brandsProducts || '').trim(),
            brandCategory: String(data.brandCategory || '').trim(),
            brandWebsite: String(data.brandWebsite || '').trim(),
            email,
            password,
            confirmPassword,
            acceptTerms
          }
        : {
            userType: 'SLIM',
            name: data.name,
            email,
            password,
            confirmPassword,
            acceptTerms
          };

    const result = await registerUser(payload);

    if (!result.success) {
      if (Array.isArray(result.errors)) {
        result.errors.forEach((e) => {
          if (e?.field && e?.message) setError(e.field, { type: 'server', message: e.message });
        });
      }
      if (String(result.error || '').toLowerCase().includes('email') && String(result.error || '').toLowerCase().includes('already')) {
        setError('email', { type: 'server', message: result.error });
      }
      return;
    }

    if (inviteToken && inviteInfo?.valid) {
      try {
        const userId = result.data?.user?._id || result.data?.user?.id || '';
        await consumeRegistrationInvitation(inviteToken, {
          email: String(email || '').trim().toLowerCase(),
          userId: String(userId || ''),
        });
      } catch {
        // Registration succeeded; invite consumption is best-effort.
      }
    }

    if (result.success) {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className={`w-full space-y-6 ${isExternalUser ? 'max-w-5xl' : 'max-w-md'}`}>
        <div className={`bg-white rounded-2xl shadow-xl border border-gray-100 ${isExternalUser ? 'p-6 lg:p-8' : 'p-8'}`}>
          {showTermsModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900">Website Terms and Conditions</h3>
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(false)}
                    className="text-gray-400 hover:text-gray-600 focus:outline-none"
                  >
                    <span className="sr-only">Close</span>
                    ✕
                  </button>
                </div>
                {/<[a-z][\s\S]*>/i.test(termsText) ? (
                  <div
                    className="px-6 py-4 overflow-y-auto text-sm text-gray-700 [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-4 [&_h1]:mb-1 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:text-gray-900 [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-sm [&_h3]:font-medium [&_h3]:text-gray-900 [&_h3]:mt-3 [&_h3]:mb-1 [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-0.5 [&_strong]:font-semibold [&_em]:italic [&_s]:line-through [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:text-gray-600 [&_blockquote]:italic [&_blockquote]:my-2 [&_hr]:border-gray-200 [&_hr]:my-3"
                    dangerouslySetInnerHTML={{ __html: termsText }}
                  />
                ) : (
                  <div className="px-6 py-4 overflow-y-auto text-sm text-gray-700">
                    <p className="whitespace-pre-wrap leading-relaxed">{termsText}</p>
                  </div>
                )}
                <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(false)}
                    className="inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="text-center">
            <div className="mx-auto h-12 w-auto flex justify-center mb-4">
              <img
                src={getSlimLogoPath()}
                alt="SLIM"
                className="h-14 w-auto"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Create your account</h2>
            <p className="mt-2 text-sm text-gray-600">
              Register to request access. Your account will have viewer-level permissions.
            </p>
            {inviteToken && inviteLoading && (
              <p className="mt-3 text-sm text-gray-500">Validating your invitation link…</p>
            )}
            {inviteToken && !inviteLoading && inviteInfo?.valid && (
              <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
                Invitation accepted for <span className="font-medium">{inviteInfo.recipientEmail}</span>.
                {inviteInfo.expiryDate && (
                  <span className="block text-xs text-emerald-800 mt-0.5">
                    Link valid until {new Date(inviteInfo.expiryDate).toLocaleString('en-GB')}.
                  </span>
                )}
              </div>
            )}
            {inviteToken && !inviteLoading && inviteInfo && !inviteInfo.valid && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {inviteInfo.message || 'This registration link is invalid or has expired.'}
              </div>
            )}
          </div>

          <form className="mt-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
            {/* User type: kept in DOM for RHF + future use; hidden — default is EXTERNAL */}
            <div className="hidden" aria-hidden="true">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">User type</label>
                <div className="flex items-center gap-6">
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      {...register('userType')}
                      type="radio"
                      value="SLIM"
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    SLIM User
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input
                      {...register('userType')}
                      type="radio"
                      value="EXTERNAL"
                      className="text-indigo-600 focus:ring-indigo-500"
                    />
                    External Company/Agency
                  </label>
                </div>
              </div>
            </div>

            {isExternalUser && (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                External accounts require admin activation before login.
              </div>
            )}

            {isExternalUser ? (
              <div className="space-y-4">
                {/* Organization type — select first so labels below adapt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Organization type <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    {[{ value: 'COMPANY', label: 'Company' }, { value: 'AGENCY', label: 'Agency' }].map(({ value, label }) => (
                      <label
                        key={value}
                        className={`flex-1 flex items-center justify-center px-4 py-2.5 border rounded-lg cursor-pointer text-sm font-medium transition-colors select-none ${
                          companyType === value
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:text-indigo-600'
                        }`}
                      >
                        <input
                          {...register('companyType', { required: 'Please select an organization type' })}
                          type="radio"
                          value={value}
                          className="sr-only"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {errors.companyType && <p className="mt-1 text-sm text-red-600">{errors.companyType.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Local / International <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    {[{ value: 'LOCAL', label: 'Local (Sri Lankan)' }, { value: 'INTERNATIONAL', label: 'International' }].map(({ value, label }) => (
                      <label
                        key={value}
                        className={`flex-1 flex items-center justify-center px-4 py-2.5 border rounded-lg cursor-pointer text-sm font-medium transition-colors select-none ${
                          companyOrigin === value
                            ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-400 hover:text-indigo-600'
                        }`}
                      >
                        <input
                          {...register('companyOrigin', { required: 'Please select Local or International' })}
                          type="radio"
                          value={value}
                          className="sr-only"
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {errors.companyOrigin && <p className="mt-1 text-sm text-red-600">{errors.companyOrigin.message}</p>}
                </div>

                {/* Personal and organization details */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('firstName', {
                        required: 'First name is required',
                        minLength: { value: 2, message: 'First name must be at least 2 characters' },
                        maxLength: { value: 30, message: 'First name must be less than 30 characters' }
                      })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.firstName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="First name"
                    />
                    {errors.firstName && <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('lastName', {
                        required: 'Last name is required',
                        minLength: { value: 2, message: 'Last name must be at least 2 characters' },
                        maxLength: { value: 30, message: 'Last name must be less than 30 characters' }
                      })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.lastName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="Last name"
                    />
                    {errors.lastName && <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>}
                  </div>

                  <Controller
                    name="mobile"
                    control={control}
                    rules={{
                      required: 'Mobile number is required',
                      validate: (value) => {
                        const result = validateStoredPhoneValue(value, 'LK', { required: true });
                        return result.isValid || 'Please enter a valid mobile number';
                      }
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        label="Mobile number"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.mobile?.message}
                        required
                        defaultCountry="LK"
                        placeholder="Enter mobile number"
                        helperText="Choose your country code and enter your number"
                      />
                    )}
                  />

                  <Controller
                    name="telephone"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const result = validateStoredPhoneValue(value, 'LK', { required: false });
                        return result.isValid || 'Please enter a valid telephone number';
                      },
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        label="Telephone"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.telephone?.message}
                        defaultCountry="LK"
                        placeholder="Enter telephone number"
                        helperText="Choose your country code and enter your number"
                      />
                    )}
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('address', { required: 'Address is required', maxLength: { value: 200, message: 'Address is too long' } })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.address ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="Address"
                    />
                    {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {entityLabel} name <span className="text-red-500">*</span>
                    </label>
                    <input
                      {...register('companyName', { required: `${entityLabel} name is required`, maxLength: { value: 120, message: `${entityLabel} name is too long` } })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.companyName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder={`${entityLabel} name`}
                    />
                    {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      V.A.T. registration no.
                    </label>
                    <input
                      {...register('vatRegistrationNo', {
                        maxLength: { value: 48, message: 'VAT number is too long' },
                        validate: (value) => {
                          if (!value || !value.trim()) return true;
                          const trimmed = value.trim();
                          if (trimmed.length < 5) return 'Enter a valid VAT number (at least 5 characters)';
                          if (!/^[A-Za-z0-9][A-Za-z0-9\-/]*$/.test(trimmed)) {
                            return 'Use letters, digits, hyphens or slashes only';
                          }
                          return true;
                        }
                      })}
                      type="text"
                      autoComplete="off"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.vatRegistrationNo ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g. 123456789-7000"
                    />
                    {errors.vatRegistrationNo && <p className="mt-1 text-sm text-red-600">{errors.vatRegistrationNo.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      TIN no.
                    </label>
                    <input
                      {...register('tin', {
                        maxLength: { value: 48, message: 'TIN number is too long' },
                        validate: (value) => {
                          if (!value || !value.trim()) return true;
                          const trimmed = value.trim();
                          if (trimmed.length < 5) return 'Enter a valid TIN number (at least 5 characters)';
                          if (!/^[A-Za-z0-9][A-Za-z0-9\-/]*$/.test(trimmed)) {
                            return 'Use letters, digits, hyphens or slashes only';
                          }
                          return true;
                        }
                      })}
                      type="text"
                      autoComplete="off"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.tin ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g. 123456789"
                    />
                    {errors.tin && <p className="mt-1 text-sm text-red-600">{errors.tin.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{entityLabel} website</label>
                    <input
                      {...register('companyWebsite', {
                        validate: (value) => {
                          if (!value) return true;
                          return /^https?:\/\//i.test(String(value)) || 'Website must start with http:// or https://';
                        }
                      })}
                      type="url"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.companyWebsite ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="https://example.com"
                    />
                    {errors.companyWebsite && <p className="mt-1 text-sm text-red-600">{errors.companyWebsite.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brands / Products</label>
                    <input
                      {...register('brandsProducts', { maxLength: { value: 500, message: 'Too long (max 500 characters)' } })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.brandsProducts ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g. Brand A, Product B"
                    />
                    {errors.brandsProducts && <p className="mt-1 text-sm text-red-600">{errors.brandsProducts.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand / Product category</label>
                    <input
                      {...register('brandCategory', { maxLength: { value: 200, message: 'Too long (max 200 characters)' } })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.brandCategory ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="e.g. FMCG, Telecom, Banking"
                    />
                    {errors.brandCategory && <p className="mt-1 text-sm text-red-600">{errors.brandCategory.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand / Product website</label>
                    <input
                      {...register('brandWebsite', {
                        validate: (value) => {
                          if (!value) return true;
                          return /^https?:\/\//i.test(String(value)) || 'Website must start with http:// or https://';
                        }
                      })}
                      type="url"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.brandWebsite ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="https://brand.example.com"
                    />
                    {errors.brandWebsite && <p className="mt-1 text-sm text-red-600">{errors.brandWebsite.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CEO name</label>
                    <input
                      {...register('ceoName', { maxLength: { value: 120, message: 'CEO name is too long' } })}
                      type="text"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.ceoName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="CEO name"
                    />
                    {errors.ceoName && <p className="mt-1 text-sm text-red-600">{errors.ceoName.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">CEO email</label>
                    <input
                      {...register('ceoEmail', {
                        validate: (value) => {
                          if (!value || !String(value).trim()) return true;
                          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim()) || 'Enter a valid email';
                        },
                      })}
                      type="email"
                      className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                        errors.ceoEmail ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                      }`}
                      placeholder="ceo@example.com"
                    />
                    {errors.ceoEmail && <p className="mt-1 text-sm text-red-600">{errors.ceoEmail.message}</p>}
                  </div>

                  <Controller
                    name="ceoMobile"
                    control={control}
                    rules={{
                      validate: (value) => {
                        const result = validateStoredPhoneValue(value, 'LK', { required: false });
                        return result.isValid || 'Please enter a valid CEO mobile number';
                      },
                    }}
                    render={({ field }) => (
                      <PhoneInput
                        label="CEO mobile"
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.ceoMobile?.message}
                        defaultCountry="LK"
                        placeholder="CEO mobile number"
                      />
                    )}
                  />

                  {companyType === 'AGENCY' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agency network</label>
                        <input
                          {...register('agencyNetwork', { maxLength: { value: 200, message: 'Too long' } })}
                          type="text"
                          className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                            errors.agencyNetwork ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Agency network"
                        />
                        {errors.agencyNetwork && <p className="mt-1 text-sm text-red-600">{errors.agencyNetwork.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agency holding company</label>
                        <input
                          {...register('agencyHoldingCompany', { maxLength: { value: 200, message: 'Too long' } })}
                          type="text"
                          className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                            errors.agencyHoldingCompany ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Holding company name"
                        />
                        {errors.agencyHoldingCompany && <p className="mt-1 text-sm text-red-600">{errors.agencyHoldingCompany.message}</p>}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full name <span className="text-red-500">*</span>
              </label>
              <input
                {...register('name', {
                  required: 'Name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters'
                  },
                  maxLength: {
                    value: 50,
                    message: 'Name must be less than 50 characters'
                  },
                  pattern: {
                    value: /^[a-zA-Z\s]+$/,
                    message: 'Name must contain only letters and spaces'
                  }
                })}
                type="text"
                className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                  errors.name
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                placeholder="Enter your full name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>
            )}

            <div className={`grid gap-4 ${isExternalUser ? 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1'}`}>
              <div className={isExternalUser ? 'md:col-span-2 xl:col-span-1' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email address <span className="text-red-500">*</span>
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  readOnly={inviteEmailLocked}
                  disabled={inviteToken && inviteLoading}
                  className={`block w-full px-3 py-2.5 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                    inviteEmailLocked ? 'bg-gray-50 cursor-not-allowed' : ''
                  } ${
                    errors.email
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="Enter your email"
                />
                {inviteEmailLocked && (
                  <p className="mt-1 text-xs text-gray-500">Email is fixed to your invitation.</p>
                )}
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('password', {
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      },
                      validate: (value) => {
                        // Keep in sync with auth-service password policy
                        // services/auth-service/src/utils/validation.js
                        const v = String(value || '');
                        const strong = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])/;
                        return (
                          strong.test(v) ||
                          'Password must include uppercase, lowercase, number, and special character (!@#$%^&*)'
                        );
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`block w-full px-3 py-2.5 pr-10 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                      errors.password
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (value) =>
                        value === passwordValue || 'Passwords do not match'
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={`block w-full px-3 py-2.5 pr-10 border rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors sm:text-sm ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                    aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start">
              <input
                {...register('acceptTerms', {
                  required: 'You must accept the terms and conditions'
                })}
                type="checkbox"
                id="acceptTerms"
                className="h-4 w-4 mt-1 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="acceptTerms" className="ml-2 block text-sm text-gray-700">
                I accept the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-indigo-600 hover:text-indigo-500 font-medium underline"
                >
                  terms and conditions
                </button>
              </label>
            </div>
            {errors.acceptTerms && (
              <p className="mt-1 text-sm text-red-600">{errors.acceptTerms.message}</p>
            )}

            <button
              type="submit"
              disabled={isSubmitting || isLoading || inviteLoading || (inviteToken && inviteInfo && !inviteInfo.valid)}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSubmitting || isLoading ? 'Creating account...' : 'Create account'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-500">
              Sign in
            </Link>
          </div>

          {/* General contact banner — shown for ALL users when configured (indigo) */}
          {generalContactInfo && (
            <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
              <p className="text-xs font-semibold text-indigo-800 mb-2">For enquiries, contact us:</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {generalContactInfo.contactPersonName && (
                  <div className="flex items-center gap-2 text-xs text-indigo-700">
                    <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{generalContactInfo.contactPersonName}</span>
                  </div>
                )}
                {generalContactInfo.contactEmail && (
                  <div className="flex items-center gap-2 text-xs text-indigo-700">
                    <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <a href={`mailto:${generalContactInfo.contactEmail}`} className="hover:underline">{generalContactInfo.contactEmail}</a>
                  </div>
                )}
                {generalContactInfo.contactPhones?.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-indigo-700">
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {generalContactInfo.contactPhones.map((phone, i) => (
                        <a key={i} href={`tel:${phone}`} className="hover:underline">{phone}</a>
                      ))}
                    </div>
                  </div>
                )}
                {generalContactInfo.address && (
                  <div className="flex items-center gap-2 text-xs text-indigo-700">
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{generalContactInfo.address}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Event contact banner — shown only for External Company/Agency users when configured (amber) */}
          {isExternalUser && contactInfo && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-xs font-semibold text-amber-800 mb-2">For enquiries on event registrations, contact us:</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {contactInfo.contactPersonName && (
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <UserIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{contactInfo.contactPersonName}</span>
                  </div>
                )}
                {contactInfo.contactEmail && (
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <EnvelopeIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <a href={`mailto:${contactInfo.contactEmail}`} className="hover:underline">{contactInfo.contactEmail}</a>
                  </div>
                )}
                {contactInfo.contactPhones?.length > 0 && (
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                      {contactInfo.contactPhones.map((phone, i) => (
                        <a key={i} href={`tel:${phone}`} className="hover:underline">{phone}</a>
                      ))}
                    </div>
                  </div>
                )}
                {contactInfo.address && (
                  <div className="flex items-center gap-2 text-xs text-amber-700">
                    <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{contactInfo.address}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Register;


